import { toast } from '@heidi/design-system/toast';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { PROJECTS_COMPOSER_MAX_STAGED_FILES } from '../../data';
import { toastAttachmentStaging } from './attachmentStagingFeedback';
import type {
  ComposerAttachmentItem,
  ComposerAttachments,
  ComposerMergePickedFiles,
} from './composer-attachments';

export type { ComposerMergePickedFiles } from './composer-attachments';

export interface UseStagedAttachmentsArgs {
  value?: File[];
  onChange?: (files: File[]) => void;
  mergePickedFiles?: ComposerMergePickedFiles;
  accept?: string;
}

export const stagedAttachmentId = (file: File, index: number): string =>
  `${file.name}-${file.size}-${index}`;

const defaultMergePickedFiles: ComposerMergePickedFiles = (
  existing,
  incoming,
  { maxCount }
) => {
  const extras = incoming ? Array.from(incoming) : [];
  return { next: [...existing, ...extras].slice(0, maxCount) };
};

export const useStagedAttachments = ({
  value,
  onChange,
  mergePickedFiles = defaultMergePickedFiles,
  accept,
}: UseStagedAttachmentsArgs = {}): ComposerAttachments => {
  const intl = useIntl();
  const [internalStagedFiles, setInternalStagedFiles] = useState<File[]>([]);
  const stagedFiles = value ?? internalStagedFiles;

  const attach = useCallback(
    (incomingFiles: FileList | readonly File[] | null) => {
      const previousCount = stagedFiles.length;
      const { next, error } = mergePickedFiles(stagedFiles, incomingFiles, {
        maxCount: PROJECTS_COMPOSER_MAX_STAGED_FILES,
        oversizedPolicy: 'skip',
      });
      const addedCount = next.length - previousCount;
      if (error && addedCount <= 0) toast({ ...error, tone: 'destructive' });
      if (value === undefined) setInternalStagedFiles(next);
      onChange?.(next);
      toastAttachmentStaging(intl, previousCount, next.length, Boolean(error));
    },
    [intl, mergePickedFiles, onChange, stagedFiles, value]
  );

  const items = useMemo<readonly ComposerAttachmentItem[]>(
    () =>
      stagedFiles.map((file, index) => ({
        id: stagedAttachmentId(file, index),
        name: file.name,
        type: file.type,
        size: file.size,
        status: 'staged' as const,
        source: { kind: 'file' as const, file },
      })),
    [stagedFiles]
  );

  const remove = useCallback(
    (id: string) => {
      const next = stagedFiles.filter(
        (file, index) => stagedAttachmentId(file, index) !== id
      );
      if (value === undefined) setInternalStagedFiles(next);
      onChange?.(next);
    },
    [onChange, stagedFiles, value]
  );

  return { items, attach, remove, readyToSend: true, accept };
};
